import { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Rating from '@mui/material/Rating';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import { useNavigate } from 'react-router-dom';
import type { GuideProfile } from '@/types';
import { Button } from '@/shared/components/ui';
import { getLowestGuidePrice, getPricingSummary } from '@/features/guide/utils/pricing';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AuthModal } from '@/features/auth/components/AuthModal';
import { BookingCheckoutSheet } from '@/features/booking/components/BookingCheckoutSheet';

const ratingSx = {
  '& .MuiRating-iconFilled': { color: 'secondary.main' },
  '& .MuiRating-iconEmpty': { color: 'divider' },
};

interface GuideDetailBookingCardProps {
  guide: GuideProfile;
  travelDate?: string;
  endDate?: string;
  city?: string;
}

export function GuideDetailBookingCard({
  guide,
  travelDate,
  endDate,
  city,
}: GuideDetailBookingCardProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const canBook = getLowestGuidePrice(guide.pricing) != null;
  const priceLabel = getPricingSummary(guide.pricing);

  const getBookPath = () => {
    const params = new URLSearchParams();
    if (travelDate) params.set('date', travelDate);
    if (endDate) params.set('endDate', endDate);
    if (city) params.set('city', city);
    const query = params.toString();
    return query ? `/guides/${guide._id}/book?${query}` : `/guides/${guide._id}/book`;
  };

  const handleBook = () => {
    if (!isAuthenticated) {
      setAuthModalOpen(true);
      return;
    }
    setCheckoutOpen(true);
  };

  const handleAuthSuccess = (route: string) => {
    setAuthModalOpen(false);
    if (route.startsWith('/dashboard') || route.startsWith('/admin')) {
      navigate(route, { replace: true });
      return;
    }
    navigate(getBookPath());
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: (t) => t.radii.lg,
          position: { md: 'sticky' },
          top: { md: 88 },
        }}
      >
        {priceLabel && (
          <Typography variant="h5" fontWeight={800} color="primary.main" gutterBottom>
            {priceLabel}
          </Typography>
        )}

        {(guide.reviewCount ?? 0) > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Rating
              value={guide.rating ?? 0}
              readOnly
              size="small"
              precision={0.1}
              sx={ratingSx}
            />
            <Typography variant="body2" fontWeight={600}>
              {(guide.rating ?? 0).toFixed(1)} · {guide.reviewCount} reviews
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {guide.yearsOfExperience != null && guide.yearsOfExperience > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {guide.yearsOfExperience}+ years of experience
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {(guide.languagesSpoken ?? []).map((lang) => (
            <Chip
              key={lang}
              label={lang}
              size="small"
              variant="outlined"
              sx={{ borderColor: 'divider', color: 'text.secondary' }}
            />
          ))}
        </Box>

        <Button
          intent="primary-dark"
          fullWidth
          size="large"
          disabled={!canBook}
          onClick={handleBook}
          sx={{ mb: 1.5 }}
        >
          Book Now
        </Button>

        {!canBook && (
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
            This guide has not set pricing yet.
          </Typography>
        )}
      </Paper>

      <BookingCheckoutSheet
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        guideId={guide._id}
        travelDate={travelDate}
        endDate={endDate}
        city={city ?? guide.city}
      />

      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onAuthSuccess={handleAuthSuccess}
        title="Sign in to book"
        subtitle="Create a free account or sign in to book this guide for your trip."
      />
    </>
  );
}
